import { Chip, Group, iconSizes, Stack } from '@common/ui'
import { IconFilter, IconUser, IconUsers, IconWorld } from '@common/ui/icons'
import { com } from '@eidu/web'
import { ReactElement } from 'react'
import EntityWithLabelAndRelated from '../../domain/entity/EntityWithLabelAndRelated'
import { findEntityTypeByFieldId, findFieldById } from '../../util/EntityTypeExtensions'
import fieldValueLabel from '../../util/FieldValueExtensions'
import EntityType = com.eidu.entity.type.EntityType
import EntityTypeId = com.eidu.entity.type.EntityTypeId
import EntityId = com.eidu.entity.EntityId
import EntitySelector = com.eidu.entity.selector.EntitySelector

type EntityTypesMap = ReadonlyMap<EntityTypeId, EntityType>
type EntitiesByTypeId = ReadonlyMap<EntityTypeId, ReadonlyMap<EntityId, EntityWithLabelAndRelated>>

const chipContent = (icon: ReactElement, label: ReactElement | string) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
    {icon}
    <span>{label}</span>
  </span>
)

const Combinator = ({ label }: { label: string }) => (
  <span
    style={{
      paddingInline: 6,
      paddingBlock: 1,
      borderRadius: 'var(--trellis-radius-xs)',
      backgroundColor: 'var(--trellis-color-surface)',
      color: 'var(--trellis-color-text-secondary)',
      fontSize: '0.7rem',
      fontWeight: 700,
      letterSpacing: 0.5,
      lineHeight: 1.6,
      alignSelf: 'center',
    }}
  >
    {label}
  </span>
)

const TypeChip = ({ name, keyPrefix }: { name: string; keyPrefix: string }) => (
  <Chip key={keyPrefix} tone="platform" style={{ fontWeight: 600 }}>
    {chipContent(<IconUsers size={iconSizes.xs} stroke={1.5} />, name)}
  </Chip>
)

const FilterChip = ({
  icon,
  fieldLabel,
  value,
  keyPrefix,
}: {
  icon: ReactElement
  fieldLabel: string
  value: ReactElement | string
  keyPrefix: string
}) => (
  <Chip key={keyPrefix} tone="neutral">
    {chipContent(
      icon,
      <span>
        {fieldLabel}
        <span style={{ marginInline: 4, color: 'var(--trellis-color-text-secondary)' }}>=</span>
        <strong>{value}</strong>
      </span>
    )}
  </Chip>
)

const renderFilterLeaf = (
  selector: EntitySelector,
  entityTypes: EntityTypesMap,
  entitiesByTypeId: EntitiesByTypeId,
  key: string
): ReactElement | null => {
  const types = [...entityTypes.values()]
  if (selector instanceof EntitySelector.FieldValueEquals) {
    const entityType = findEntityTypeByFieldId(types, selector.fieldId)
    const field = entityType ? findFieldById(entityType, selector.fieldId) : undefined
    const fieldLabel = field?.name ?? 'unknown field'
    const value = field ? fieldValueLabel(field, selector.value, entitiesByTypeId) ?? '(loading…)' : '(loading…)'
    return (
      <FilterChip
        key={key}
        keyPrefix={key}
        icon={<IconFilter size={iconSizes.xs} stroke={1.5} />}
        fieldLabel={fieldLabel}
        value={value}
      />
    )
  }
  if (selector instanceof EntitySelector.FieldValueEqualsUserEntityId) {
    const entityType = findEntityTypeByFieldId(types, selector.fieldId)
    const field = entityType ? findFieldById(entityType, selector.fieldId) : undefined
    const fieldLabel = field?.name ?? 'unknown field'
    return (
      <FilterChip
        key={key}
        keyPrefix={key}
        icon={<IconUser size={iconSizes.xs} stroke={1.5} />}
        fieldLabel={fieldLabel}
        value="current user"
      />
    )
  }
  if (selector instanceof EntitySelector.FieldValueEqualsUserFieldValue) {
    const entityType = findEntityTypeByFieldId(types, selector.fieldId)
    const userEntityType = findEntityTypeByFieldId(types, selector.userFieldId)
    const field = entityType ? findFieldById(entityType, selector.fieldId) : undefined
    const userField = userEntityType ? findFieldById(userEntityType, selector.userFieldId) : undefined
    const fieldLabel = field?.name ?? 'unknown field'
    const userFieldLabel = userEntityType && userField ? `user.${userEntityType.name}.${userField.name}` : 'user field'
    return (
      <FilterChip
        key={key}
        keyPrefix={key}
        icon={<IconUser size={iconSizes.xs} stroke={1.5} />}
        fieldLabel={fieldLabel}
        value={userFieldLabel}
      />
    )
  }
  return null
}

const filterSelectorTypeId = (selector: EntitySelector, entityTypes: EntityTypesMap): EntityTypeId | undefined => {
  const types = [...entityTypes.values()]
  if (
    selector instanceof EntitySelector.FieldValueEquals ||
    selector instanceof EntitySelector.FieldValueEqualsUserEntityId ||
    selector instanceof EntitySelector.FieldValueEqualsUserFieldValue
  )
    return findEntityTypeByFieldId(types, selector.fieldId)?.id
  return undefined
}

const AllUsersChip = ({ keyPrefix }: { keyPrefix?: string }) => (
  <Chip key={keyPrefix} tone="neutral">
    {chipContent(<IconWorld size={iconSizes.xs} stroke={1.5} />, 'All users')}
  </Chip>
)

const renderRecursive = (
  selector: EntitySelector,
  entityTypes: EntityTypesMap,
  entitiesByTypeId: EntitiesByTypeId,
  key: string
): ReactElement => {
  if (EntitySelector.All.equals(selector)) return <AllUsersChip key={key} keyPrefix={key} />

  if (selector instanceof EntitySelector.OfType) {
    const name = entityTypes.get(selector.typeId)?.name ?? `Unknown type (${selector.typeId})`
    return <TypeChip keyPrefix={key} name={name} />
  }

  const filterChip = renderFilterLeaf(selector, entityTypes, entitiesByTypeId, key)
  if (filterChip) return filterChip

  if (selector instanceof EntitySelector.And || selector instanceof EntitySelector.Or) {
    const combinator = selector instanceof EntitySelector.And ? 'AND' : 'OR'
    const children = [...selector.selectors.asJsReadonlyArrayView()]
    return (
      <Group key={key} gap="xxs" align="center" style={{ flexWrap: 'wrap' }}>
        {children.flatMap((child, index) => {
          const node = renderRecursive(child, entityTypes, entitiesByTypeId, `${key}-${index}`)
          return index === 0 ? [node] : [<Combinator key={`${key}-c-${index}`} label={combinator} />, node]
        })}
      </Group>
    )
  }

  if (selector instanceof EntitySelector.Not) {
    return (
      <Group key={key} gap="xxs" align="center" wrap="nowrap">
        <Combinator label="NOT" />
        {renderRecursive(selector.selector, entityTypes, entitiesByTypeId, `${key}-n`)}
      </Group>
    )
  }

  return (
    <Chip key={key} tone="neutral">
      {selector.toString()}
    </Chip>
  )
}

const renderSummarized = (
  selector: EntitySelector,
  entityTypes: EntityTypesMap,
  entitiesByTypeId: EntitiesByTypeId,
  direction: 'row' | 'column'
): ReactElement | null => {
  if (EntitySelector.All.equals(selector)) return <AllUsersChip />

  const children = selector instanceof EntitySelector.And ? [...selector.selectors.asJsReadonlyArrayView()] : [selector]

  const typeIds = new Map<string, EntityTypeId>()
  const filterSelectors: EntitySelector[] = []

  for (const child of children) {
    if (child instanceof EntitySelector.OfType) {
      typeIds.set(child.typeId.toString(), child.typeId)
    } else if (
      child instanceof EntitySelector.FieldValueEquals ||
      child instanceof EntitySelector.FieldValueEqualsUserEntityId ||
      child instanceof EntitySelector.FieldValueEqualsUserFieldValue
    ) {
      const inferred = filterSelectorTypeId(child, entityTypes)
      if (inferred) typeIds.set(inferred.toString(), inferred)
      filterSelectors.push(child)
    } else {
      return null
    }
  }

  const typeChips = [...typeIds.values()].map((id, index) => {
    const name = entityTypes.get(id)?.name ?? `Unknown type (${id})`
    return <TypeChip key={`type-${index}`} keyPrefix={`type-${index}`} name={name} />
  })

  const filterChips = filterSelectors.map(
    (child, index) => renderFilterLeaf(child, entityTypes, entitiesByTypeId, `filter-${index}`)!
  )

  if (typeChips.length === 0 && filterChips.length === 0) return null

  if (direction === 'row') {
    return (
      <Group gap="xxs" align="center" style={{ flexWrap: 'wrap' }}>
        {typeChips}
        {filterChips}
      </Group>
    )
  }

  return (
    <Stack gap="xxs" align="flex-start">
      {typeChips.length > 0 && (
        <Group gap="xxs" align="center" style={{ flexWrap: 'wrap' }}>
          {typeChips}
        </Group>
      )}
      {filterChips.length > 0 && (
        <Group gap="xxs" align="center" style={{ flexWrap: 'wrap' }}>
          {filterChips}
        </Group>
      )}
    </Stack>
  )
}

type EntitySelectorChipsProps = {
  selector: EntitySelector
  entityTypes: EntityTypesMap
  entitiesByTypeId: EntitiesByTypeId
  direction?: 'row' | 'column'
}

const EntitySelectorChips = ({
  selector,
  entityTypes,
  entitiesByTypeId,
  direction = 'column',
}: EntitySelectorChipsProps) => {
  const summarized = renderSummarized(selector, entityTypes, entitiesByTypeId, direction)
  return (
    <div style={{ paddingBlock: 8 }}>
      {summarized ?? renderRecursive(selector, entityTypes, entitiesByTypeId, 'root')}
    </div>
  )
}

export default EntitySelectorChips
